/**
 * Achievement data contract. BLUEPRINT section 23.
 *
 * An achievement is a row in `src/data/achievements.json` with a `condition`
 * written in the small DSL below. The evaluator in `evaluator.ts` is the only
 * place a condition is interpreted, so adding an achievement is a JSON entry
 * whenever an existing condition kind fits, and a new kind plus a case in the
 * evaluator plus a field on `MatchSummary` when it does not.
 *
 * Nothing here knows a card name or a card id.
 */

import type { Faction } from '@/engine';

export type AchievementCategory =
  | 'general'
  | 'combat'
  | 'deckbuilding'
  | 'faction'
  | 'collection'
  | 'ranked'
  | 'secret';

export type AchievementTier = 'bronze' | 'silver' | 'gold' | 'prismatic';

/** Lifetime counters the profile store keeps. */
export type StatField = 'wins' | 'losses' | 'gamesPlayed' | 'rating';

/** Counters the achievements store keeps, because nothing else needed them. */
export type CounterField = 'packsOpened' | 'genesisPulled' | 'savedDecks';

/** Boolean fields a finished match reports. */
export type MatchFlagField =
  | 'redrew'
  | 'deckEmptyAtWin'
  | 'lostIntegrity'
  | 'exactLethal'
  | 'drainerLinkWin'
  | 'frontrunKill'
  | 'blocked4Survived';

/** Numeric fields a finished match reports. */
export type MatchValueField =
  | 'turns'
  | 'playerIntegrityEnd'
  | 'minIntegrity'
  | 'maxDamageOneCombat'
  | 'maxDestroyedByOneCard'
  | 'deckFactionCount'
  | 'legendariesControlledAtOnce'
  | 'earliestExpensiveDeploy'
  | 'maxAttackControlled';

export type AchievementCondition =
  /** A lifetime profile stat reached a threshold. */
  | { kind: 'stat'; stat: StatField; min: number }
  /** A store or deck builder counter reached a threshold. */
  | { kind: 'counter'; counter: CounterField; min: number }
  /** The tutorial was finished. */
  | { kind: 'tutorialComplete' }
  /** The match just played reported this flag. `win` also requires a win. */
  | { kind: 'matchFlag'; flag: MatchFlagField; value?: boolean; win?: boolean }
  /** The match just played reported a number inside this range. */
  | { kind: 'matchValue'; field: MatchValueField; min?: number; max?: number; win?: boolean }
  /** A win has been taken with a mono deck of this faction, ever. */
  | { kind: 'monoFactionWin'; faction: Faction }
  /** A mono win has been taken with all five factions. */
  | { kind: 'allMonoFactionWins' }
  /** At least one copy of every card in the set is owned. */
  | { kind: 'ownEveryCard' }
  /** Each named cosmetic slot holds something other than the free starter. */
  | { kind: 'equipped'; slots: ('sleeve' | 'playmat' | 'banner')[] };

export interface Achievement {
  id: string;
  name: string;
  category: AchievementCategory;
  tier: AchievementTier;
  /** Which mark the badge draws. A faction id or a category glyph name. */
  glyph: string;
  faction?: Faction;
  description: string;
  /** How to get it, in one line, shown under the description. */
  unlockText: string;
  condition: AchievementCondition;
  secret: boolean;
  /** Shown in place of the description while a secret one is locked. */
  hint?: string;
}

export const TIER_ORDER: AchievementTier[] = ['bronze', 'silver', 'gold', 'prismatic'];

export const CATEGORY_ORDER: AchievementCategory[] = [
  'general',
  'combat',
  'deckbuilding',
  'faction',
  'collection',
  'ranked',
  'secret',
];

export const CATEGORY_LABEL: Record<AchievementCategory, string> = {
  general: 'General',
  combat: 'Combat',
  deckbuilding: 'Deck building',
  faction: 'Faction',
  collection: 'Collection',
  ranked: 'Ranked',
  secret: 'Secret',
};

export const TIER_LABEL: Record<AchievementTier, string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  prismatic: 'Prismatic',
};
